// Test builds only: the settings of site.config.json that the end-to-end tests need switched on, so that the ads and
// the sale are exercised although production has them off (docs/plans/local-validation.md). VITE_PREVIEW_ADS=1 turns
// the ads on, with stand-in banners served by the preview server (/__affiliate/, tests/e2e/helpers.ts) where the
// affiliate panel's aren't filled in; VITE_PREVIEW_SALES=1 offers Remove Ads. Both move the launch into the past. A
// production build never takes them: vite.config.ts refuses them, and so do the release checks.
export const PREVIEW_LAUNCH_AT = '2026-01-01T00:00:00+09:00';
/** Where the test builds' stand-in Plugin Boutique banners and their links come from. */
export const PREVIEW_AFFILIATE_BASE = '/__affiliate/';

export interface PreviewEnv {
  readonly MODE?: string;
  readonly VITE_PREVIEW_ADS?: string;
  readonly VITE_PREVIEW_SALES?: string;
}

interface Previewable {
  launch: { at: string };
  ads: {
    enabled: boolean;
    affiliate?: { creatives: { id: string; image: string | null; href: string | null }[] };
  };
  product: { removeAds: { offered: boolean } };
}

/** The preview flags a build asks for (never in production mode). */
export function previewFlags(env: PreviewEnv): { ads: boolean; sales: boolean } {
  if (env.MODE === 'production') return { ads: false, sales: false };
  return { ads: env.VITE_PREVIEW_ADS === '1', sales: env.VITE_PREVIEW_SALES === '1' };
}

/** site.config.json as a test build with these flags sees it; unchanged without them. */
export function previewConfig<T extends Previewable>(config: T, env: PreviewEnv): T {
  const { ads, sales } = previewFlags(env);
  if (!ads && !sales) return config;
  const creatives = config.ads.affiliate?.creatives ?? [];
  return {
    ...config,
    launch: { ...config.launch, at: PREVIEW_LAUNCH_AT },
    ads: ads
      ? {
          ...config.ads,
          enabled: true,
          affiliate: {
            ...config.ads.affiliate,
            creatives: creatives.map((creative) => ({
              ...creative,
              image: `${PREVIEW_AFFILIATE_BASE}banner-${creative.id}.png`,
              href: `${PREVIEW_AFFILIATE_BASE}click/${creative.id}`,
            })),
          },
        }
      : config.ads,
    product: sales ? { ...config.product, removeAds: { ...config.product.removeAds, offered: true } } : config.product,
  };
}
